import { Modal, Image, Select, Row, Spin } from "antd";
import {
  type MouseEvent,
  type UIEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import styles from "@/styles/addOnDealsModal.module.scss";
import CartIconPlus from "@/svgs/icon-add-on.svg";
import ChangeSvg from "@/svgs/change.svg";
import AddOnSuccess from "@/svgs/icon-add-on-success.svg";
import InfoSvg from "@/svgs/icon-Info.svg";
import TabBar from "./TabBar";
import { formatCurrency } from "@/utils/utils";
import addOnApi from "@/service/modules/addOn";
import Link from "next/link";
import useCartActions from "@/utils/addTocartAction";
import AddOnInfoModal from "./productDetail/AddOnInfoModal";
import getConfig from "next/config";
import useAddOnCartItems from "@/utils/useAddOnCartItems";
const { publicRuntimeConfig } = getConfig();
const imageDomain = publicRuntimeConfig.imageDomain;

const { Option } = Select;
const ADD_ON_PAGE_SIZE = 20;
const RECOMMENDED_VISIBLE_COUNT = 7;
const CATEGORY_VISIBLE_COUNT = 5;

interface AddOnDealsModalProps {
  show: boolean;
  setShow: (_show: boolean) => void;
  onAddOnSuccess?: () => void;
  subtotal?: number;
  mainProduct?: {
    productID?: string | number;
    qty: string | number;
  };
}

interface AddOnProduct {
  productID: number;
  name: string;
  addonPrice: number;
  normalPrice: number;
  image: string;
  appSelectStatus: number;
  url: string;
}

interface AddOnCategory {
  id: number;
  name: string;
}

const normalizeProduct = (item: any): AddOnProduct => {
  return {
    productID: item.productID,
    name: item.name,
    addonPrice: Number(item.addonPrice),
    normalPrice: Number(item.normalPrice),
    image: item.filename || item.newThumbnail || item.thumbnail,
    appSelectStatus: item.appSelectStatus,
    url: item.url,
  };
};

const getProductList = (payload: any) => {
  const list = payload?.data?.list || payload?.data?.items || payload?.data || [];
  return Array.isArray(list) ? list : [];
};

const mergeRecommendedProducts = (
  selectedProduct: AddOnProduct | null,
  redZoneProducts: AddOnProduct[],
) => {
  const nextProducts = selectedProduct ? [selectedProduct] : [];
  const productIDs = new Set(nextProducts.map((item) => item.productID));

  redZoneProducts.forEach((product) => {
    if (productIDs.has(product.productID)) return;
    productIDs.add(product.productID);
    nextProducts.push(product);
  });

  return nextProducts.slice(0, RECOMMENDED_VISIBLE_COUNT);
};

const normalizeCategoryItem = (item: any): AddOnCategory | null => {
  const id = Number(item?.cateId);
  const name = item?.cateName;

  if (Number.isNaN(id) || !name) return null;

  return {
    id,
    name: String(name),
  };
};

const normalizeCategoryMap = (categoryMap: any): AddOnCategory[] => {
  if (!Array.isArray(categoryMap)) return [];

  return categoryMap
    .map(normalizeCategoryItem)
    .filter((item): item is AddOnCategory => !!item);
};

const normalizeCategories = (payload: any): AddOnCategory[] => {
  const responseData = payload?.data && typeof payload.data === "object"
    ? payload.data
    : payload;
  return normalizeCategoryMap(responseData?.categoryMap);
};

export default function AddOnDealsModal({
  show,
  setShow,
  onAddOnSuccess,
  subtotal,
  mainProduct,
}: AddOnDealsModalProps) {
  const [selectedDropdown, setSelectedDropdown] = useState<
    string | undefined
  >();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(-1);
  const [recommendedProducts, setRecommendedProducts] = useState<
    AddOnProduct[]
  >([]);
  const [products, setProducts] = useState<AddOnProduct[]>([]);
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [recommendedTotal, setRecommendedTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentCategoryID, setCurrentCategoryID] = useState<
    number | undefined
  >();
  const [addingProductIDs, setAddingProductIDs] = useState<
    Record<number, boolean>
  >({});
  const { addToCart, batchAddToCart } = useCartActions();
  const getAddOnCartItems = useAddOnCartItems(mainProduct);
  const tabCategories = categories.slice(0, CATEGORY_VISIBLE_COUNT);
  const dropdownCategories = categories.slice(CATEGORY_VISIBLE_COUNT);
  const showCategoryProducts = recommendedTotal > RECOMMENDED_VISIBLE_COUNT;

  const getSelectedRecommendedProduct = async () => {
    try {
      const res = await addOnApi.getSelectedAddOns();
      const selectedList = getProductList(res)
        .map(normalizeProduct)
        .filter((item) => item.productID && item.image);
      return selectedList[0] || null;
    } catch (error) {
      console.error("Selected add-on data acquisition failed:", error);
      return null;
    }
  };

  const getRecommendedProducts = useCallback(async () => {
    try {
      setRecommendedLoading(true);
      const params: any = { perPage: RECOMMENDED_VISIBLE_COUNT + 1 };
      if (subtotal !== undefined) params.subtotal = subtotal;
      const [selectedProduct, res] = await Promise.all([
        getSelectedRecommendedProduct(),
        addOnApi.getAddOnRedZone(params),
      ]);
      const list = getProductList(res).map(normalizeProduct);
      const total =
        Number(
          res?.data?.total ??
          res?.data?.count ??
          res?.data?.totalCount ??
          res?.data?.pagination?.total,
        ) || list.length;
      setRecommendedTotal(total);
      setRecommendedProducts(mergeRecommendedProducts(selectedProduct, list));
      return total > RECOMMENDED_VISIBLE_COUNT;
    } catch (error) {
      console.error("Add-on red zone data acquisition failed:", error);
      setRecommendedProducts([]);
      setRecommendedTotal(0);
      return false;
    } finally {
      setRecommendedLoading(false);
    }
  }, [subtotal]);

  const getAddOnCategories = useCallback(async () => {
    try {
      const res = await addOnApi.getAddOnCateMap({ subtotal });
      const nextCategories = normalizeCategories(res?.data);
      setCategories(nextCategories);
      return nextCategories;
    } catch (error) {
      console.error("Add-on category data acquisition failed:", error);
      setCategories([]);
      return [];
    }
  }, [subtotal]);

  const getAddOnProducts = useCallback(
    async (categoryID?: number, nextPage = 1, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setProductsLoading(true);
        }

        const params: any = {
          page: nextPage,
          perPage: ADD_ON_PAGE_SIZE,
          subtotal,
        };
        if (categoryID !== undefined) params.categoryID = categoryID;

        const res = await addOnApi.getAddOnList(params);
        const list = (res?.data?.list || []).map(normalizeProduct);
        setProducts((prev) => (append ? [...prev, ...list] : list));
        setPage(nextPage);
        setHasMore(list.length === ADD_ON_PAGE_SIZE);
      } catch (error) {
        console.error("Add-on list data acquisition failed:", error);
        if (!append) setProducts([]);
      } finally {
        setProductsLoading(false);
        setLoadingMore(false);
      }
    },
    [subtotal],
  );

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    const category = tabCategories[index];
    if (category) {
      setSelectedDropdown(undefined);
      setCurrentCategoryID(category.id);
      setPage(1);
      setHasMore(true);
      getAddOnProducts(category.id);
    }
  };

  const handleDropdownChange = (value: string) => {
    const category = dropdownCategories.find(
      (item) => String(item.id) === value,
    );
    setSelectedDropdown(value);
    setDropdownOpen(false);
    setActiveTab(-1);
    if (category) {
      setCurrentCategoryID(category.id);
      setPage(1);
      setHasMore(true);
      getAddOnProducts(category.id);
    }
  };

  const handleContentScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!showCategoryProducts) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
    if (!isNearBottom || productsLoading || loadingMore || !hasMore) return;
    getAddOnProducts(currentCategoryID, page + 1, true);
  };

  const handleAddToCart = (event: MouseEvent, product: AddOnProduct) => {
    event.stopPropagation();
    if (addingProductIDs[product.productID]) return;

    setAddingProductIDs((prev) => ({
      ...prev,
      [product.productID]: true,
    }));
    const callback = (response: any) => {
      setAddingProductIDs((prev) => {
        const next = { ...prev };
        delete next[product.productID];
        return next;
      });
      if (response === "error") return;
      const updateProductStatus = (item: AddOnProduct) =>
        item.productID === product.productID
          ? { ...item, appSelectStatus: 1 }
          : item;
      setRecommendedProducts((prev) => prev.map(updateProductStatus));
      setProducts((prev) => prev.map(updateProductStatus));
      onAddOnSuccess?.();
    };

    const productList = getAddOnCartItems(product.productID);
    if (productList.length > 1) {
      batchAddToCart(
        event,
        { productList },
        callback,
      );
      return;
    }

    addToCart(
      event,
      { productID: product.productID, qty: 1, from: 1 },
      callback,
    );
  };

  const renderAddCartAction = (product: AddOnProduct) => {
    if (product.appSelectStatus) {
      return <AddOnSuccess className={styles["selected-icon"]} />;
    }

    if (addingProductIDs[product.productID]) {
      return <Spin size="small" className={styles["cart-loading"]} />;
    }

    return (
      <CartIconPlus
        className={styles["cart-icon"]}
        onClick={(event: MouseEvent) => handleAddToCart(event, product)}
      />
    );
  };

  useEffect(() => {
    if (!show) return;
    const initAddOnProducts = async () => {
      setSelectedDropdown(undefined);
      setActiveTab(-1);
      setCurrentCategoryID(undefined);
      setPage(1);
      setHasMore(true);
      setProducts([]);
      setCategories([]);

      const shouldShowCategoryProducts = await getRecommendedProducts();
      if (!shouldShowCategoryProducts) return;

      const nextCategories = await getAddOnCategories();
      const firstCategory = nextCategories[0];
      if (firstCategory) {
        setActiveTab(0);
        setCurrentCategoryID(firstCategory.id);
        getAddOnProducts(firstCategory.id);
      }
    };

    initAddOnProducts();
  }, [show, getAddOnCategories, getRecommendedProducts, getAddOnProducts]);

  return (
    <>
      <AddOnInfoModal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />
      <Modal
        open={show}
        onCancel={() => {
          setDropdownOpen(false);
          setShow(false);
        }}
        footer={null}
        width={1240}
        centered
        className={styles["addOnDealsModal"]}
        closable={true}
      >
        <div
          className={styles["modal-content"]}
          onScroll={handleContentScroll}
          style={dropdownOpen ? { overflow: "hidden" } : undefined}
        >
          <div className={styles["modal-header"]}>
            <div className={styles["header-title"]}>
              Super Value Add-on Deals
              <InfoSvg
                className={styles["info-icon"]}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowInfoModal(true);
                }}
              />
            </div>
          </div>

          <div className={styles["recommended-section"]}>
            <div className={styles["recommended-header"]}>
              <span className={styles["recommended-title"]}>
                Recommended Add-ons
              </span>
              <span className={styles["limited-stock-tag"]}>
                Limited Stock - Grab it Fast!
              </span>
              {showCategoryProducts && (
                <div
                  className={styles["recommen-change"]}
                  onClick={getRecommendedProducts}
                >
                  Change
                  <ChangeSvg style={{ width: 14, marginLeft: 6 }} />
                </div>
              )}
            </div>

            <div className={styles["recommended-list"]}>
              {recommendedLoading ? (
                <div className={styles["loading-box"]}>
                  <Spin />
                </div>
              ) : (
                recommendedProducts.map((product) => (
                  <div
                    className={styles["recommended-item"]}
                    key={product.productID}
                  >
                    <div className={styles["product-image"]}>
                      <Link href={product.url} legacyBehavior>
                        <a rel="noopener noreferrer" target="_blank">
                          <Image
                            src={product.image}
                            alt={product.name}
                            width={140}
                            height={140}
                            preview={false}
                          />
                        </a>
                      </Link>
                    </div>
                    <Link href={product.url} legacyBehavior>
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        className={styles["product-name"]}
                      >
                        {product.name}
                      </a>
                    </Link>
                    <Row
                      justify="space-between"
                      align="middle"
                      className={styles["product-price-row"]}
                    >
                      <div className={styles["product-price"]}>
                        <span className={styles["current-price"]}>
                          {formatCurrency(product.addonPrice)}
                        </span>
                        <span className={styles["original-price"]}>
                          {formatCurrency(product.normalPrice)}
                        </span>
                      </div>
                      <div className={styles["add-cart-btn"]}>
                        {renderAddCartAction(product)}
                      </div>
                    </Row>
                  </div>
                ))
              )}
            </div>

            <div className={styles["lightning-image"]}>
              <Image
                src={`${imageDomain}/add-on/lightning.png`}
                alt=""
                preview={false}
                width={72}
                height={118}
              />
            </div>
          </div>

          {showCategoryProducts && (
            <>
              <div className={styles["category-section"]}>
                <div className={styles["category-tabs"]}>
                  <TabBar
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    tabItemRightMargin="30px"
                    tabList={tabCategories}
                  />

                  {dropdownCategories.length > 0 && (
                    <div className={styles["category-dropdown"]}>
                      <Select
                        style={{ width: 180 }}
                        value={selectedDropdown}
                        placeholder="More"
                        onChange={handleDropdownChange}
                        onDropdownVisibleChange={setDropdownOpen}
                        className={styles["dropdown-select"]}
                        getPopupContainer={(triggerNode) =>
                          triggerNode.parentElement || document.body
                        }
                      >
                        {dropdownCategories.map((category) => (
                          <Option value={String(category.id)} key={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles["products-grid"]}>
                {productsLoading ? (
                  <div className={styles["loading-box"]}>
                    <Spin />
                  </div>
                ) : (
                  products.map((product) => (
                    <div className={styles["product-card"]} key={product.productID}>
                      <div className={styles["product-image"]}>
                        <Link href={product.url} legacyBehavior>
                          <a rel="noopener noreferrer" target="_blank">
                            <Image
                              src={product.image}
                              alt={product.name}
                              width={200}
                              height={200}
                              preview={false}
                            />
                          </a>
                        </Link>
                      </div>
                      <Link href={product.url} legacyBehavior>
                        <a
                          rel="noopener noreferrer"
                          target="_blank"
                          className={styles["product-name"]}
                        >
                          {product.name}
                        </a>
                      </Link>
                      <div className={styles["product-price-row"]}>
                        <div className={styles["product-price"]}>
                          <span className={styles["current-price"]}>
                            {formatCurrency(product.addonPrice)}
                          </span>
                          <span className={styles["original-price"]}>
                            {formatCurrency(product.normalPrice)}
                          </span>
                        </div>
                        <div className={styles["add-cart-btn"]}>
                          {renderAddCartAction(product)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {loadingMore && (
                  <div className={styles["load-more-box"]}>
                    <Spin />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
